import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Navbar from "../../../components/layout/Navbar/Navbar";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import EstabelecimentoSaudeHeader from "../../../components/Estabelecimento-Saude/EstabelecimentoSaudeHeader";
import ConfirmacaoEstabelecimentoSaude from "../../../components/Estabelecimento-Saude/EstabelecimentoSaudeConfirm";
import { getCargoIdSidebar } from "../../../constants/sidebarConfig";
import apiService from "../../../services/api";
import editIcon from "../../../assets/edit.png";

const NAVBAR_HEIGHT = 65;
const SIDEBAR_WIDTH = 300;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9f9f9;
  font-family: public-sans, sans-serif;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 60px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
`;

const ListContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1.5fr 0.8fr 1.6fr 1.5fr 80px;
  align-items: center;
  height: 50px;
  background: #f2f0f0;
  font-size: 18px;
  font-weight: 700;
  color: #6ab8a8;
  gap: 0;

  > div {
    padding: 0 12px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1.5fr 0.8fr 1.6fr 1.5fr 80px;
  align-items: center;
  height: 48px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  &:hover {
    background: #f9fefa;
  }

  > div {
    padding: 0 12px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const EmptyMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: #555;
  font-size: 14px;
`;

const LoadingMessage = styled(EmptyMessage)``;

const ErrorMessage = styled(EmptyMessage)`
  color: #c53030;
`;

const SmallText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;

  &:hover {
    opacity: 0.8;
  }
`;

const ListaEstabelecimentosSaude = ({ onEditItem }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiService.getEstabelecimentosSaude();
        const data = Array.isArray(result) ? result : result?.data || [];
        setItems(data);
      } catch (err) {
        setError(err.message || "Falha ao carregar estabelecimentos.");
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  return (
    <ListContainer>
      <HeaderRow>
        <div>ID</div>
        <div>Nome do estabelecimento</div>
        <div>Tipo</div>
        <div>Diretor</div>
        <div>Responsável técnico / estágio</div>
        <div>Ações</div>
      </HeaderRow>

      {loading && <LoadingMessage>Carregando estabelecimentos...</LoadingMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {!loading && !error && items.length === 0 && (
        <EmptyMessage>Nenhum estabelecimento de saúde cadastrado.</EmptyMessage>
      )}

      <ListWrapper>
        {items.map((est) => (
          <ListRow key={est.id}>
            <div>{est.id}</div>
            <div>{est.nome}</div>
            <div>{est.tipo || <SmallText>—</SmallText>}</div>
            <div>
              {est.nomeDiretor ? (
                <>
                  {est.nomeDiretor}
                  {est.cargoDiretor && (
                    <SmallText style={{ marginLeft: 6 }}>
                      ({est.cargoDiretor})
                    </SmallText>
                  )}
                </>
              ) : (
                <SmallText>—</SmallText>
              )}
            </div>
            <div>
              {est.nomeResponsavelTecnico ? (
                <>
                  {est.nomeResponsavelTecnico}
                  {est.cargoResponsavelTecnico && (
                    <SmallText style={{ marginLeft: 6 }}>
                      ({est.cargoResponsavelTecnico})
                    </SmallText>
                  )}
                </>
              ) : (
                <SmallText>—</SmallText>
              )}
            </div>
            <ActionsCell>
              <IconButton
                type="button"
                title="Editar estabelecimento"
                onClick={() => onEditItem && onEditItem(est)}
              >
                <img src={editIcon} alt="Editar" style={{ width: 22, height: 22 }} />
              </IconButton>
            </ActionsCell>
          </ListRow>
        ))}
      </ListWrapper>
    </ListContainer>
  );
};

const EstabelecimentosSaude = () => {
  const [user, setUser] = useState(null);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [estabelecimentoUsuario, setEstabelecimentoUsuario] = useState(null);
  const [loadingEstabelecimento, setLoadingEstabelecimento] = useState(false);
  const [errorEstabelecimento, setErrorEstabelecimento] = useState(null);
  const [selectedEstabelecimento, setSelectedEstabelecimento] = useState(null);
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        const filteredItems = getCargoIdSidebar(userData.cargoId);
        setSidebarItems(filteredItems);

        if (userData.cargoId === 4 && userData.estabelecimentoSaudeId) {
          setLoadingEstabelecimento(true);
          setErrorEstabelecimento(null);
          try {
            const est = await apiService.getEstabelecimentosSaudeId(
              userData.estabelecimentoSaudeId
            );
            setEstabelecimentoUsuario(est);
          } catch (err) {
            setErrorEstabelecimento(
              err.message ||
                "Erro ao carregar dados do estabelecimento do usuário."
            );
          } finally {
            setLoadingEstabelecimento(false);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar usuário:", err);
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, []);

  const dataConfirm =
    user?.cargoId === 4 && estabelecimentoUsuario
      ? {
          nomeEstabelecimento: estabelecimentoUsuario.nome,
          nomeDiretor: estabelecimentoUsuario.nomeDiretor,
          cargoDiretor: estabelecimentoUsuario.cargoDiretor,
          nomeDiretorTecnico: estabelecimentoUsuario.nomeDiretorTecnico,
          cargoDiretorTecnico: estabelecimentoUsuario.cargoDiretorTecnico,
          nomeResponsavelEstagio:
            estabelecimentoUsuario.nomeResponsavelTecnico,
          cargoResponsavelEstagio:
            estabelecimentoUsuario.cargoResponsavelTecnico,
        }
      : null;

  return (
    <PageWrapper>
      <Navbar
        onLogout={apiService.logout}
        showHome={true}
        showNotifications={true}
        showProfile={true}
        showLogout={true}
        user={user}
      />
      <ContentWrapper>
        <Sidebar items={sidebarItems} />
        <MainContent>
          <EstabelecimentoSaudeHeader />

          {loadingUser && (
            <EmptyMessage>Carregando informações do usuário...</EmptyMessage>
          )}

          {!loadingUser && user?.cargoId === 1 && (
            <>
              {selectedEstabelecimento ? (
                <ConfirmacaoEstabelecimentoSaude
                  data={{
                    nomeEstabelecimento: selectedEstabelecimento.nome,
                    nomeDiretor: selectedEstabelecimento.nomeDiretor,
                    cargoDiretor: selectedEstabelecimento.cargoDiretor,
                    nomeDiretorTecnico: selectedEstabelecimento.nomeDiretorTecnico,
                    cargoDiretorTecnico: selectedEstabelecimento.cargoDiretorTecnico,
                    nomeResponsavelEstagio:
                      selectedEstabelecimento.nomeResponsavelTecnico,
                    cargoResponsavelEstagio:
                      selectedEstabelecimento.cargoResponsavelTecnico,
                  }}
                  enableInlineEdit
                  estabelecimentoId={selectedEstabelecimento.id}
                  onUpdated={(updated) => {
                    if (updated) {
                      setSelectedEstabelecimento(null);
                      setListVersion((prev) => prev + 1);
                    }
                  }}
                  onClose={() => {
                    setSelectedEstabelecimento(null);
                  }}
                />
              ) : (
                <ListaEstabelecimentosSaude
                  key={listVersion}
                  onEditItem={(est) => setSelectedEstabelecimento(est)}
                />
              )}
            </>
          )}

          {!loadingUser && user?.cargoId === 4 && (
            <>
              {loadingEstabelecimento && (
                <LoadingMessage>
                  Carregando dados do estabelecimento...
                </LoadingMessage>
              )}
              {errorEstabelecimento && (
                <ErrorMessage>{errorEstabelecimento}</ErrorMessage>
              )}
              {!loadingEstabelecimento && !errorEstabelecimento && dataConfirm && (
                <ConfirmacaoEstabelecimentoSaude
                  data={dataConfirm}
                  enableInlineEdit
                  estabelecimentoId={estabelecimentoUsuario.id}
                  onUpdated={(updated) => {
                    if (updated) {
                      setEstabelecimentoUsuario(updated);
                    }
                  }}
                />
              )}
              {!loadingEstabelecimento &&
                !errorEstabelecimento &&
                !dataConfirm && (
                  <EmptyMessage>
                    Nenhum estabelecimento de saúde vinculado ao seu usuário.
                  </EmptyMessage>
                )}
            </>
          )}
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default EstabelecimentosSaude;
